import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

declare global {
  interface Window {
    WOW: any;
  }
}

interface EventFormData {
  eventType: string;
  fullName: string;
  email: string;
  contactNumber: string;
  organizationName: string;
  preferredDate: string;
  alternateDate: string;
  numberOfAttendees: string;
  eventDetails: string;
  donationItems: string;
  specialRequirements: string;
  agreedToTerms: boolean;
}

const SponsorEvent: React.FC = () => {
  const [formData, setFormData] = useState<EventFormData>({
    eventType: '',
    fullName: '',
    email: '',
    contactNumber: '',
    organizationName: '',
    preferredDate: '',
    alternateDate: '',
    numberOfAttendees: '',
    eventDetails: '',
    donationItems: '',
    specialRequirements: '',
    agreedToTerms: false
  });

  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Custom checkbox/radio styles
  const checkboxStyles = useMemo(() => `
  .custom-checkbox {
    position: relative;
    display: inline-block;
    cursor: pointer;
    user-select: none;
  }

  .custom-checkbox input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  .custom-checkbox .checkbox-box {
    position: relative;
    display: inline-block;
    width: 22px;
    height: 22px;
    background-color: white;
    border: 2px solid #ddd;
    border-radius: 5px;
    transition: all 0.3s ease;
  }

  .custom-checkbox:hover .checkbox-box {
    border-color: #03A84A;
  }

  .custom-checkbox input[type="checkbox"]:checked ~ .checkbox-box {
    background-color: #03A84A;
    border-color: #03A84A;
  }

  .custom-checkbox .checkbox-box::after {
    content: "";
    position: absolute;
    display: none;
    left: 6px;
    top: 2px;
    width: 6px;
    height: 11px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  .custom-checkbox input[type="checkbox"]:checked ~ .checkbox-box::after {
    display: block;
    animation: checkmark 0.2s ease-in-out;
  }

  @keyframes checkmark {
    0% { transform: rotate(45deg) scale(0); }
    100% { transform: rotate(45deg) scale(1); }
  }

  .custom-radio {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }

  .custom-radio input[type="radio"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  .custom-radio .radio-box {
    position: relative;
    display: inline-block;
    width: 20px;
    height: 20px;
    background-color: white;
    border: 2px solid #ddd;
    border-radius: 50%;
    transition: all 0.3s ease;
    margin-right: 8px;
  }

  .custom-radio:hover .radio-box {
    border-color: #03A84A;
  }

  .custom-radio input[type="radio"]:checked ~ .radio-box {
    border-color: #03A84A;
  }

  .custom-radio .radio-box::after {
    content: "";
    position: absolute;
    display: none;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #03A84A;
  }

  .custom-radio input[type="radio"]:checked ~ .radio-box::after {
    display: block;
    animation: radiomark 0.2s ease-in-out;
  }

  @keyframes radiomark {
    0% { transform: translate(-50%, -50%) scale(0); }
    100% { transform: translate(-50%, -50%) scale(1); }
  }
  `, []);

  useEffect(() => {
    // Initialize data-background attributes
    const elements = document.querySelectorAll('[data-background]');
    elements.forEach((element) => {
      const bg = element.getAttribute('data-background');
      if (bg) {
        (element as HTMLElement).style.backgroundImage = `url(${bg})`;
      }
    });

    // Initialize WOW.js for scroll animations
    if (window.WOW) {
      const wow = new window.WOW({
        boxClass: 'wow',
        animateClass: 'animated',
        offset: 0,
        mobile: true,
        live: true
      });
      wow.init();
    }

    // Inject custom styles
    const styleElement = document.createElement('style');
    styleElement.innerHTML = checkboxStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [checkboxStyles]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (value: string) => {
    setFormData(prev => ({ ...prev, eventType: value }));
  };

  const handleCheckboxChange = () => {
    setFormData(prev => ({ ...prev, agreedToTerms: !prev.agreedToTerms }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agreedToTerms) {
      setSubmitError('Please agree to the terms and conditions before submitting');
      setTimeout(() => setSubmitError(''), 3000);
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError('');
      setSubmitMessage('');

      const response = await fetch('http://localhost:5000/api/event-sponsorships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSubmitMessage('Thank you! Your event request has been submitted successfully. We will contact you soon.');
        setSubmitError('');
        setFormData({
          eventType: '',
          fullName: '',
          email: '',
          contactNumber: '',
          organizationName: '',
          preferredDate: '',
          alternateDate: '',
          numberOfAttendees: '',
          eventDetails: '',
          donationItems: '',
          specialRequirements: '',
          agreedToTerms: false
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setSubmitMessage(''), 5000);
      } else {
        setSubmitError(data?.message || 'Failed to submit. Please try again.');
        setTimeout(() => setSubmitError(''), 3000);
      }
    } catch (error) {
      setSubmitError('An error occurred. Please try again later.');
      setTimeout(() => setSubmitError(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e8e8e8',
    borderRadius: '8px',
    fontSize: '15px',
    transition: 'border-color 0.3s ease',
    fontFamily: 'inherit'
  };

  return (
    <>
      {/* Page Banner */}
      <section 
        className="banner__inner-page bg-image pt-160 pb-160" 
        data-background="/assets/images/banner/banner-inner-page.jpg"
      >
        <div className="container">
          <h2 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">Sponsor an Event</h2>
          <div className="breadcrumb-list wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
            <Link to="/">Home</Link>
            <span><i className="fa-regular fa-angles-right mx-2"></i>Sponsor an Event</span>
          </div>
        </div>
      </section>

      {/* Event Sponsorship Section */}
      <section style={{ padding: '80px 0', backgroundColor: '#f9fafb' }}>
        <div className="container">
          {submitMessage && (
            <div style={{
              padding: '15px 20px',
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '8px',
              color: '#155724',
              marginBottom: '30px',
              textAlign: 'center'
            }}>
              {submitMessage}
            </div>
          )}

          {submitError && (
            <div style={{
              padding: '15px 20px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '8px',
              color: '#721c24',
              marginBottom: '30px',
              textAlign: 'center'
            }}>
              {submitError}
            </div>
          )}

          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Introduction */}
              <div className="wow fadeInUp" style={{
                backgroundColor: 'white',
                padding: '40px',
                borderRadius: '15px',
                boxShadow: '0 5px 30px rgba(0, 0, 0, 0.08)',
                marginBottom: '30px',
                textAlign: 'center'
              }}>
                <h3 style={{ 
                  color: '#0C2239', 
                  fontSize: '32px', 
                  fontWeight: '700',
                  marginBottom: '20px'
                }}>
                  Make Your Special Day More Meaningful
                </h3>
                <p style={{ 
                  color: '#666', 
                  fontSize: '16px', 
                  lineHeight: '1.8',
                  marginBottom: '20px'
                }}>
                  Celebrate your birthday, organize a donation program, or host a special event with our children. 
                  Your kindness brings joy to their lives and creates lasting memories for everyone.
                </p>
                <div style={{
                  backgroundColor: '#FFF5F2',
                  padding: '25px',
                  borderRadius: '10px',
                  border: '2px solid #FFE5DC'
                }}>
                  <div className="row">
                    <div className="col-md-4 mb-3 mb-md-0">
                      <i className="fa-solid fa-cake-candles" style={{ fontSize: '36px', color: '#03A84A', marginBottom: '10px' }}></i>
                      <h5 style={{ color: '#0C2239', fontSize: '18px', fontWeight: '600' }}>Birthday Celebrations</h5>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '0' }}>Celebrate with our children</p>
                    </div>
                    <div className="col-md-4 mb-3 mb-md-0">
                      <i className="fa-solid fa-hand-holding-heart" style={{ fontSize: '36px', color: '#03A84A', marginBottom: '10px' }}></i>
                      <h5 style={{ color: '#0C2239', fontSize: '18px', fontWeight: '600' }}>Donation Programs</h5>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '0' }}>Distribute clothes, food & more</p>
                    </div>
                    <div className="col-md-4">
                      <i className="fa-solid fa-heart" style={{ fontSize: '36px', color: '#03A84A', marginBottom: '10px' }}></i>
                      <h5 style={{ color: '#0C2239', fontSize: '18px', fontWeight: '600' }}>Special Events</h5>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '0' }}>Create memorable moments</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Request Form */}
              <form onSubmit={handleSubmit}>
                {/* Event Type Selection */}
                <div className="wow fadeInUp" data-wow-delay="100ms" style={{
                  backgroundColor: '#FFF5F2',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(247, 79, 34, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #FFE5DC'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '25px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-calendar-check me-2" style={{ color: '#03A84A' }}></i>
                    Event Type <span style={{ color: '#03A84A' }}>*</span>
                  </h4>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <label className="custom-radio" style={{
                      padding: '15px 20px',
                      backgroundColor: formData.eventType === 'Birthday Celebration' ? 'white' : '#f9f9f9',
                      border: `2px solid ${formData.eventType === 'Birthday Celebration' ? '#03A84A' : '#e8e8e8'}`,
                      borderRadius: '10px',
                      transition: 'all 0.3s ease'
                    }}>
                      <input
                        type="radio"
                        name="eventType"
                        value="Birthday Celebration"
                        checked={formData.eventType === 'Birthday Celebration'}
                        onChange={() => handleRadioChange('Birthday Celebration')}
                        required
                      />
                      <span className="radio-box"></span>
                      <div>
                        <strong style={{ color: '#0C2239', display: 'block', marginBottom: '5px' }}>
                          🎂 Birthday Celebration
                        </strong>
                        <span style={{ color: '#666', fontSize: '14px' }}>
                          Celebrate your special day with our children - bring food, cake, and enjoy together
                        </span>
                      </div>
                    </label>

                    <label className="custom-radio" style={{
                      padding: '15px 20px',
                      backgroundColor: formData.eventType === 'Donation Program' ? 'white' : '#f9f9f9',
                      border: `2px solid ${formData.eventType === 'Donation Program' ? '#03A84A' : '#e8e8e8'}`,
                      borderRadius: '10px',
                      transition: 'all 0.3s ease'
                    }}>
                      <input
                        type="radio"
                        name="eventType"
                        value="Donation Program"
                        checked={formData.eventType === 'Donation Program'}
                        onChange={() => handleRadioChange('Donation Program')}
                        required
                      />
                      <span className="radio-box"></span>
                      <div>
                        <strong style={{ color: '#0C2239', display: 'block', marginBottom: '5px' }}>
                          🎁 Donation Program
                        </strong>
                        <span style={{ color: '#666', fontSize: '14px' }}>
                          Host a donation event - distribute clothes, supplies, or other items with your team or NGO
                        </span>
                      </div>
                    </label>

                    <label className="custom-radio" style={{
                      padding: '15px 20px',
                      backgroundColor: formData.eventType === 'Other Event' ? 'white' : '#f9f9f9',
                      border: `2px solid ${formData.eventType === 'Other Event' ? '#03A84A' : '#e8e8e8'}`,
                      borderRadius: '10px',
                      transition: 'all 0.3s ease'
                    }}>
                      <input
                        type="radio"
                        name="eventType"
                        value="Other Event"
                        checked={formData.eventType === 'Other Event'}
                        onChange={() => handleRadioChange('Other Event')}
                        required
                      />
                      <span className="radio-box"></span>
                      <div>
                        <strong style={{ color: '#0C2239', display: 'block', marginBottom: '5px' }}>
                          ✨ Other Special Event
                        </strong>
                        <span style={{ color: '#666', fontSize: '14px' }}>
                          Have another idea? Let us know how you'd like to support our children
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="wow fadeInUp" data-wow-delay="200ms" style={{
                  backgroundColor: '#FFF5F2',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(247, 79, 34, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #FFE5DC'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '25px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-user me-2" style={{ color: '#03A84A' }}></i>
                    Contact Information
                  </h4>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Full Name <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Email Address <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Contact Number <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="tel"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleInputChange}
                        required
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Organization Name (if applicable)
                      </label>
                      <input
                        type="text"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleInputChange}
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>
                  </div>
                </div>

                {/* Event Details */}
                <div className="wow fadeInUp" data-wow-delay="300ms" style={{
                  backgroundColor: '#FFF5F2',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(247, 79, 34, 0.1)',
                  marginBottom: '30px',
                  border: '2px solid #FFE5DC'
                }}>
                  <h4 style={{ 
                    color: '#0C2239', 
                    paddingBottom: '15px',
                    marginBottom: '25px',
                    fontSize: '22px',
                    fontWeight: '700',
                    borderBottom: '3px solid #03A84A',
                    display: 'inline-block'
                  }}>
                    <i className="fa-solid fa-calendar-days me-2" style={{ color: '#03A84A' }}></i>
                    Event Details
                  </h4>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Preferred Date <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="date"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Alternate Date
                      </label>
                      <input
                        type="date"
                        name="alternateDate"
                        value={formData.alternateDate}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split('T')[0]}
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Number of Attendees (from your side) <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <input
                        type="number"
                        name="numberOfAttendees"
                        value={formData.numberOfAttendees}
                        onChange={handleInputChange}
                        required
                        min="1"
                        placeholder="How many people will come with you?"
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Event Details <span style={{ color: '#03A84A' }}>*</span>
                      </label>
                      <textarea
                        name="eventDetails"
                        value={formData.eventDetails}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        placeholder="Describe what you plan to do during the event (e.g., bring food, organize games, give speeches, etc.)"
                        style={{...inputStyle, resize: 'vertical'}}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>

                    {formData.eventType === 'Donation Program' && (
                      <div className="col-md-12 mb-3">
                        <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                          Donation Items <span style={{ color: '#03A84A' }}>*</span>
                        </label>
                        <textarea
                          name="donationItems"
                          value={formData.donationItems}
                          onChange={handleInputChange}
                          required={formData.eventType === 'Donation Program'}
                          rows={3}
                          placeholder="List the items you plan to donate (e.g., clothes, books, toys, food items)"
                          style={{...inputStyle, resize: 'vertical'}}
                          onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                          onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                        />
                      </div>
                    )}

                    <div className="col-md-12 mb-3">
                      <label style={{ color: '#0C2239', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Special Requirements / Additional Notes
                      </label>
                      <textarea
                        name="specialRequirements"
                        value={formData.specialRequirements}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="Any special arrangements or requirements?"
                        style={{...inputStyle, resize: 'vertical'}}
                        onFocus={(e) => e.target.style.borderColor = '#03A84A'}
                        onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
                      />
                    </div>
                  </div>
                </div>

                {/* Terms & Submit */}
                <div className="wow fadeInUp" data-wow-delay="400ms" style={{
                  backgroundColor: 'white',
                  padding: '30px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 30px rgba(0, 0, 0, 0.08)',
                  marginBottom: '30px'
                }}>
                  <label className="custom-checkbox" style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '15px',
                    backgroundColor: '#FFF5F2',
                    border: '2px solid #FFE5DC',
                    borderRadius: '10px'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.agreedToTerms}
                      onChange={handleCheckboxChange}
                      style={{ display: 'none' }}
                    />
                    <span className="checkbox-box" style={{ marginTop: '2px', flexShrink: 0 }}></span>
                    <span style={{ color: '#0C2239', lineHeight: '1.6' }}>
                      I understand that my event request is subject to approval and availability. 
                      The New Life Nepal team will contact me to confirm the date and discuss event arrangements. 
                      I agree to follow all organizational guidelines during my visit. <span style={{ color: '#03A84A' }}>*</span>
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '16px',
                      backgroundColor: isSubmitting ? '#7fc79b' : '#03A84A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '18px',
                      fontWeight: '700',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      marginTop: '20px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 5px 20px rgba(247, 79, 34, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      if (isSubmitting) return;
                      e.currentTarget.style.backgroundColor = '#d63d15';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      if (isSubmitting) return;
                      e.currentTarget.style.backgroundColor = '#03A84A';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="fa-solid fa-paper-plane me-2"></i>
                    {isSubmitting ? 'Submitting...' : 'Submit Event Request'}
                  </button>
                </div>
              </form>

              {/* Contact Info */}
              <div className="wow fadeInUp" data-wow-delay="500ms" style={{
                backgroundColor: '#E8F4FD',
                padding: '30px',
                borderRadius: '15px',
                textAlign: 'center'
              }}>
                <h5 style={{ color: '#0C2239', fontWeight: '700', marginBottom: '15px' }}>
                  Have Questions? Contact Us
                </h5>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  <i className="fa-solid fa-phone me-2" style={{ color: '#03A84A' }}></i>
                  <a href="tel:+9779851031573" style={{ color: '#0C2239', textDecoration: 'none' }}>
                    +977 9851031573
                  </a>
                </p>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  <i className="fa-solid fa-envelope me-2" style={{ color: '#03A84A' }}></i>
                  <a href="mailto:info@newlifenepal.org" style={{ color: '#0C2239', textDecoration: 'none' }}>
                    info@newlifenepal.org
                  </a>
                </p>
                <p style={{ color: '#666', marginBottom: '0' }}>
                  <i className="fa-solid fa-location-dot me-2" style={{ color: '#03A84A' }}></i>
                  Gokarneshwor-5, Kathmandu, Nepal
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SponsorEvent;
